import { useRef, useState } from 'react';
import { Paperclip, X } from 'lucide-react';
import { Button, toast } from '@/shared/ui';
import { draftsApi } from '@/shared/api/drafts';
import { DEFAULT_FORM_FILES, formFileValues, type FormFileValue } from '@/shared/api/forms';
import { attachmentIcon, attachmentKind, formatBytes } from '../lib/attachments';
import styles from './FileField.module.css';

const MAX_FILE_MB = 20;

let uploadSeq = 0;

interface Uploading {
  key: string;
  name: string;
  percent: number;
}

export const FileField: React.FC<{
  id?: string;
  value: unknown;
  maxFiles?: number;
  disabled?: boolean;
  onChange: (files: FormFileValue[] | undefined) => void;
}> = ({ id, value, maxFiles = DEFAULT_FORM_FILES, disabled, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const files = formFileValues(value);
  const [uploading, setUploading] = useState<Uploading[]>([]);
  const latest = useRef(files);
  latest.current = files;

  const room = maxFiles - files.length - uploading.length;

  const pick = async (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const chosen = Array.from(list);
    if (chosen.length > room) toast.warning(`В этом поле не больше ${maxFiles} файлов`);
    for (const file of chosen.slice(0, Math.max(0, room))) {
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`«${file.name}» больше ${MAX_FILE_MB} МБ`);
        continue;
      }
      const key = `${file.name}-${++uploadSeq}`;
      setUploading((prev) => [...prev, { key, name: file.name, percent: 0 }]);
      try {
        const uploaded = await draftsApi.uploadFile(file, (percent) =>
          setUploading((prev) => prev.map((u) => (u.key === key ? { ...u, percent } : u))),
        );
        const next = [...latest.current, uploaded];
        latest.current = next;
        onChange(next);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Не удалось загрузить файл');
      } finally {
        setUploading((prev) => prev.filter((u) => u.key !== key));
      }
    }
  };

  const remove = (file: FormFileValue) => {
    const next = latest.current.filter((f) => f.id !== file.id);
    latest.current = next;
    onChange(next.length > 0 ? next : undefined);
    void draftsApi.deleteFile(file.id).catch(() => undefined);
  };

  return (
    <div id={id} className={styles.root}>
      {(files.length > 0 || uploading.length > 0) && (
        <ul className={styles.list}>
          {files.map((file) => {
            const Icon = attachmentIcon(attachmentKind(file.mime ?? '', file.name));
            return (
              <li key={file.id} className={styles.item}>
                <Icon size={16} className={styles.icon} aria-hidden />
                <span className={styles.name}>{file.name}</span>
                {typeof file.size === 'number' && <span className={styles.size}>{formatBytes(file.size)}</span>}
                <button
                  type="button"
                  className={styles.remove}
                  disabled={disabled}
                  onClick={() => remove(file)}
                  aria-label={`Убрать ${file.name}`}
                >
                  <X size={14} aria-hidden />
                </button>
              </li>
            );
          })}
          {uploading.map((u) => (
            <li key={u.key} className={styles.item} aria-busy>
              <Paperclip size={16} className={styles.icon} aria-hidden />
              <span className={styles.name}>{u.name}</span>
              <span className={styles.size}>{u.percent}%</span>
            </li>
          ))}
        </ul>
      )}

      {room > 0 && (
        <Button
          className={styles.add}
          variant="secondary"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          <Paperclip size={14} aria-hidden />
          {files.length === 0 ? 'Прикрепить файл' : 'Добавить ещё'}
        </Button>
      )}
      <input
        ref={inputRef}
        type="file"
        multiple={maxFiles > 1}
        hidden
        onChange={(e) => {
          void pick(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
};
